import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';
import {
  DollarSign, TrendingUp, TrendingDown, Shield, AlertTriangle,
  Clock, Users, Heart, FileText, Download, Calculator,
  BarChart3, PieChart, LineChart, CheckCircle, Save
} from 'lucide-react';
import {
  BarChart, Bar, LineChart as RechartsLine, Line, PieChart as RechartsPie, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROIDashboard = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState(null);

  // Insurance Premium Calculator State
  const [insurance, setInsurance] = useState({
    currentPremium: 50000,
    employeeCount: 100,
    incidentsLastYear: 5,
    incidentsThisYear: 2,
    violationsPrevented: 3
  });

  // OSHA Fine Calculator State
  const [oshaFines, setOshaFines] = useState({
    seriousViolations: 0,
    willfulViolations: 0,
    repeatedViolations: 0,
    failureToAbateDays: 0
  });

  // Productivity Gains State
  const [productivity, setProductivity] = useState({
    auditsPerMonth: 4,
    oshaLogsPerMonth: 1,
    reportsPerMonth: 2,
    trainingSessionsPerMonth: 2,
    safetyManagerHourlyRate: 50
  });

  // Medical Costs State
  const [medicalCosts, setMedicalCosts] = useState({
    minorInjuriesPrevented: 0,
    medicalTreatmentPrevented: 0,
    lostTimeInjuriesPrevented: 0,
    seriousInjuriesPrevented: 0
  });

  // Staff Comparison State
  const [staffing, setStaffing] = useState({
    totalEmployees: 100,
    appSubscriptionCost: 29.99 // Monthly cost (Professional plan - most common)
  });

  // Enhanced User Input State - Real Company Data
  const [companyData, setCompanyData] = useState({
    // Safety Staff Costs
    numSafetyEmployees: 1,
    avgSafetyEmployeeSalary: 80000,
    safetyEmployeeBenefitsPercentage: 30,
    
    // Injury Data - Current Year
    currentYearInjuries: {
      minorInjuries: 0, // First aid only
      medicalTreatment: 0, // Requires medical but no lost time
      lostTimeInjuries: 0, // Days away from work
      seriousInjuries: 0, // Hospitalization/permanent disability
      fatalities: 0
    },
    
    // Injury Costs - User's Actual Costs
    injuryCosts: {
      minorInjuryCost: 1500,
      medicalTreatmentCost: 42000,
      lostTimeInjuryCost: 47000,
      seriousInjuryCost: 1400000,
      fatalityCost: 4000000
    },
    
    // Previous Year Baseline for Comparison
    previousYearInjuries: {
      minorInjuries: 0,
      medicalTreatment: 0,
      lostTimeInjuries: 0,
      seriousInjuries: 0,
      fatalities: 0
    },
    
    // Additional Costs
    currentInsurancePremium: 50000,
    oshaFinesLastYear: 0,
    oshaFinesThisYear: 0
  });

  // Calculate Insurance Savings
  const calculateInsuranceSavings = () => {
    const incidentReduction = insurance.incidentsLastYear - insurance.incidentsThisYear;
    const reductionPercentage = (incidentReduction / Math.max(insurance.incidentsLastYear, 1)) * 100;
    
    // Base premium reduction: 2-5% per year without incidents
    let premiumReduction = 0;
    if (insurance.incidentsThisYear === 0) {
      premiumReduction = 0.05; // 5% for zero incidents
    } else if (incidentReduction > 0) {
      premiumReduction = 0.02 + (reductionPercentage / 100 * 0.03); // 2-5% based on reduction
    }
    
    // Additional reduction for violations prevented (1% per violation)
    premiumReduction += (insurance.violationsPrevented * 0.01);
    
    // Cap at 25% reduction per year
    premiumReduction = Math.min(premiumReduction, 0.25);
    
    const annualSavings = insurance.currentPremium * premiumReduction;
    const threeYearSavings = annualSavings * 3 * 1.1; // Compound 10% over 3 years
    const fiveYearSavings = annualSavings * 5 * 1.15; // Compound 15% over 5 years
    
    return {
      annualSavings: Math.round(annualSavings),
      threeYearSavings: Math.round(threeYearSavings),
      fiveYearSavings: Math.round(fiveYearSavings),
      reductionPercentage: Math.round(premiumReduction * 100)
    };
  };

  // Calculate OSHA Fine Savings
  const calculateOSHAFineSavings = () => {
    const SERIOUS_FINE = 16131;
    const WILLFUL_FINE = 161323;
    const FAILURE_TO_ABATE = 16131;
    
    const seriousSavings = oshaFines.seriousViolations * SERIOUS_FINE;
    const willfulSavings = oshaFines.willfulViolations * WILLFUL_FINE;
    const repeatedSavings = oshaFines.repeatedViolations * WILLFUL_FINE;
    const failureToAbateSavings = oshaFines.failureToAbateDays * FAILURE_TO_ABATE;
    
    const totalSavings = seriousSavings + willfulSavings + repeatedSavings + failureToAbateSavings;
    
    return {
      seriousSavings,
      willfulSavings,
      repeatedSavings,
      failureToAbateSavings,
      totalSavings
    };
  };

  // Calculate Productivity Gains
  const calculateProductivityGains = () => {
    // Time savings per activity
    const auditTimeSaved = 6; // 8 hours manual -> 2 hours with app
    const oshaLogTimeSaved = 1.5; // 2 hours -> 30 min
    const reportTimeSaved = 3.75; // 4 hours -> 15 min
    const trainingTimeSaved = 2.5; // 3 hours -> 30 min
    
    const monthlyHoursSaved = 
      (productivity.auditsPerMonth * auditTimeSaved) +
      (productivity.oshaLogsPerMonth * oshaLogTimeSaved) +
      (productivity.reportsPerMonth * reportTimeSaved) +
      (productivity.trainingSessionsPerMonth * trainingTimeSaved);
    
    const monthlySavings = monthlyHoursSaved * productivity.safetyManagerHourlyRate;
    const annualSavings = monthlySavings * 12;
    
    return {
      monthlyHoursSaved: Math.round(monthlyHoursSaved * 10) / 10,
      annualHoursSaved: Math.round(monthlyHoursSaved * 12),
      monthlySavings: Math.round(monthlySavings),
      annualSavings: Math.round(annualSavings)
    };
  };

  // Calculate Medical Cost Savings
  const calculateMedicalCostSavings = () => {
    const MINOR_INJURY_COST = 1500;
    const MEDICAL_TREATMENT_COST = 42000;
    const LOST_TIME_COST = 47000;
    const SERIOUS_INJURY_COST = 1400000;
    
    const minorSavings = medicalCosts.minorInjuriesPrevented * MINOR_INJURY_COST;
    const medicalSavings = medicalCosts.medicalTreatmentPrevented * MEDICAL_TREATMENT_COST;
    const lostTimeSavings = medicalCosts.lostTimeInjuriesPrevented * LOST_TIME_COST;
    const seriousSavings = medicalCosts.seriousInjuriesPrevented * SERIOUS_INJURY_COST;
    
    const totalSavings = minorSavings + medicalSavings + lostTimeSavings + seriousSavings;
    
    return {
      minorSavings,
      medicalSavings,
      lostTimeSavings,
      seriousSavings,
      totalSavings
    };
  };

  // Calculate Staff Cost Comparison
  const calculateStaffComparison = () => {
    const BASE_SALARY = 80000;
    const BENEFITS_MULTIPLIER = 1.3; // 30% benefits
    const TOTAL_COST_PER_EMPLOYEE = BASE_SALARY * BENEFITS_MULTIPLIER; // $104,000
    
    // Calculate required safety staff based on company size
    let requiredSafetyStaff = 0;
    if (staffing.totalEmployees >= 10 && staffing.totalEmployees < 50) {
      requiredSafetyStaff = 1;
    } else if (staffing.totalEmployees >= 50) {
      requiredSafetyStaff = Math.ceil(staffing.totalEmployees / 50);
    }
    
    const traditionalAnnualCost = requiredSafetyStaff * TOTAL_COST_PER_EMPLOYEE;
    const appAnnualCost = staffing.appSubscriptionCost * 12;
    const annualSavings = traditionalAnnualCost - appAnnualCost;
    const fiveYearSavings = annualSavings * 5;
    
    const savingsPercentage = traditionalAnnualCost > 0 
      ? Math.round((annualSavings / traditionalAnnualCost) * 100)
      : 0;
    
    return {
      requiredSafetyStaff,
      traditionalAnnualCost,
      appAnnualCost,
      annualSavings,
      fiveYearSavings,
      savingsPercentage
    };
  };

  // NEW: Calculate Real Company Costs based on User Input
  const calculateRealCompanyCosts = () => {
    // Current Safety Staff Costs
    const benefitsMultiplier = 1 + (companyData.safetyEmployeeBenefitsPercentage / 100);
    const totalSafetyStaffCost = companyData.numSafetyEmployees * 
      companyData.avgSafetyEmployeeSalary * benefitsMultiplier;
    
    // Current Year Injury Costs
    const currentInjuryCosts = 
      (companyData.currentYearInjuries.minorInjuries * companyData.injuryCosts.minorInjuryCost) +
      (companyData.currentYearInjuries.medicalTreatment * companyData.injuryCosts.medicalTreatmentCost) +
      (companyData.currentYearInjuries.lostTimeInjuries * companyData.injuryCosts.lostTimeInjuryCost) +
      (companyData.currentYearInjuries.seriousInjuries * companyData.injuryCosts.seriousInjuryCost) +
      (companyData.currentYearInjuries.fatalities * companyData.injuryCosts.fatalityCost);
    
    // Previous Year Injury Costs (Baseline)
    const previousInjuryCosts = 
      (companyData.previousYearInjuries.minorInjuries * companyData.injuryCosts.minorInjuryCost) +
      (companyData.previousYearInjuries.medicalTreatment * companyData.injuryCosts.medicalTreatmentCost) +
      (companyData.previousYearInjuries.lostTimeInjuries * companyData.injuryCosts.lostTimeInjuryCost) +
      (companyData.previousYearInjuries.seriousInjuries * companyData.injuryCosts.seriousInjuryCost) +
      (companyData.previousYearInjuries.fatalities * companyData.injuryCosts.fatalityCost);
    
    // Injury Reduction (Savings)
    const injuryReductionSavings = previousInjuryCosts - currentInjuryCosts;
    
    // Total Current Costs (What you're spending on safety)
    const totalCurrentSafetyCosts = 
      totalSafetyStaffCost + 
      currentInjuryCosts + 
      companyData.currentInsurancePremium + 
      companyData.oshaFinesThisYear;
    
    // Projected costs with app (replace staff cost with app cost)
    const appAnnualCost = staffing.appSubscriptionCost * 12;
    const projectedCostsWithApp = 
      appAnnualCost + 
      (currentInjuryCosts * 0.5) + // Assume 50% injury reduction with app
      (companyData.currentInsurancePremium * 0.85) + // 15% insurance reduction
      (companyData.oshaFinesThisYear * 0.3); // 70% fine reduction
    
    // Savings
    const totalAnnualSavings = totalCurrentSafetyCosts - projectedCostsWithApp;
    const roi = appAnnualCost > 0 ? Math.round((totalAnnualSavings / appAnnualCost) * 100) : 0;
    const paybackMonths = totalAnnualSavings > 0 ? Math.round((appAnnualCost / (totalAnnualSavings / 12)) * 10) / 10 : 0;
    
    return {
      totalSafetyStaffCost,
      currentInjuryCosts,
      previousInjuryCosts,
      injuryReductionSavings,
      totalCurrentSafetyCosts,
      projectedCostsWithApp,
      totalAnnualSavings,
      appAnnualCost,
      roi,
      paybackMonths,
      fiveYearSavings: totalAnnualSavings * 5,
      
      // Breakdown
      staffCostSavings: totalSafetyStaffCost - appAnnualCost,
      injuryCostSavings: currentInjuryCosts * 0.5,
      insuranceSavings: companyData.currentInsurancePremium * 0.15,
      fineSavings: companyData.oshaFinesThisYear * 0.7
    };
  };

  // Calculate Total ROI
  const calculateTotalROI = () => {
    const insuranceSavings = calculateInsuranceSavings();
    const oshaFineSavings = calculateOSHAFineSavings();
    const productivityGains = calculateProductivityGains();
    const medicalCostSavings = calculateMedicalCostSavings();
    const staffComparison = calculateStaffComparison();
    
    const totalAnnualSavings = 
      insuranceSavings.annualSavings +
      oshaFineSavings.totalSavings +
      productivityGains.annualSavings +
      medicalCostSavings.totalSavings +
      staffComparison.annualSavings;
    
    const appAnnualCost = staffComparison.appAnnualCost;
    const netAnnualSavings = totalAnnualSavings - appAnnualCost;
    const roi = appAnnualCost > 0 ? Math.round((netAnnualSavings / appAnnualCost) * 100) : 0;
    const paybackMonths = appAnnualCost > 0 ? Math.round((appAnnualCost / (netAnnualSavings / 12)) * 10) / 10 : 0;
    
    return {
      totalAnnualSavings,
      appAnnualCost,
      netAnnualSavings,
      roi,
      paybackMonths,
      fiveYearProjection: netAnnualSavings * 5
    };
  };

  // Save Calculation
  const saveCalculation = async () => {
    setLoading(true);
    try {
      const calculationData = {
        insurance,
        oshaFines,
        productivity,
        medicalCosts,
        staffing,
        companyData,
        results: {
          insurance: calculateInsuranceSavings(),
          oshaFines: calculateOSHAFineSavings(),
          productivity: calculateProductivityGains(),
          medicalCosts: calculateMedicalCostSavings(),
          staffComparison: calculateStaffComparison(),
          realCompanyCosts: calculateRealCompanyCosts(),
          totalROI: calculateTotalROI()
        }
      };

      await axios.post(`${API}/roi/calculations`, calculationData);
      
      toast({
        title: "Success",
        description: "ROI calculation saved successfully"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save calculation",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Export to PDF (mock for now)
  const exportToPDF = () => {
    toast({
      title: "Export",
      description: "PDF export feature coming soon"
    });
  };

  const totalROI = calculateTotalROI();
  const insuranceSavings = calculateInsuranceSavings();
  const oshaFineSavings = calculateOSHAFineSavings();
  const productivityGains = calculateProductivityGains();
  const medicalCostSavings = calculateMedicalCostSavings();
  const staffComparison = calculateStaffComparison();
  const realCompanyCosts = calculateRealCompanyCosts();

  // Chart data
  const savingsBreakdownData = [
    { name: 'Staff Savings', value: staffComparison.annualSavings, color: '#3b82f6' },
    { name: 'Insurance', value: insuranceSavings.annualSavings, color: '#10b981' },
    { name: 'OSHA Fines', value: oshaFineSavings.totalSavings, color: '#f59e0b' },
    { name: 'Productivity', value: productivityGains.annualSavings, color: '#8b5cf6' },
    { name: 'Medical Costs', value: medicalCostSavings.totalSavings, color: '#ef4444' }
  ].filter(item => item.value > 0);

  const fiveYearProjectionData = [
    { year: 'Year 1', savings: totalROI.netAnnualSavings },
    { year: 'Year 2', savings: totalROI.netAnnualSavings * 2 },
    { year: 'Year 3', savings: totalROI.netAnnualSavings * 3 },
    { year: 'Year 4', savings: totalROI.netAnnualSavings * 4 },
    { year: 'Year 5', savings: totalROI.netAnnualSavings * 5 }
  ];

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header with Professional ROI Analysis Image */}
      <div className="relative rounded-2xl overflow-hidden bg-gradient-to-r from-emerald-600 to-green-700 text-white shadow-xl">
        <div className="absolute inset-0 opacity-25">
          <img 
            src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=1200&q=80" 
            alt="Professional reviewing financial ROI reports"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative px-8 py-10 flex items-center justify-between">
          <div className="flex-1">
            <DollarSign className="w-16 h-16 mb-4" />
            <h1 className="text-4xl font-bold mb-2">ROI Dashboard</h1>
            <p className="text-green-100 text-lg">Calculate your return on investment and cost savings</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={saveCalculation} disabled={loading} size="lg" className="bg-white text-green-600 hover:bg-green-50">
              <Save className="w-5 h-5 mr-2" />
              Save Calculation
            </Button>
            <Button variant="outline" onClick={exportToPDF} size="lg" className="bg-white/10 backdrop-blur hover:bg-white/20 text-white border-white/30">
              <Download className="w-5 h-5 mr-2" />
              Export PDF
            </Button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-green-100 text-sm font-medium">Total Annual Savings</p>
              <TrendingUp className="w-5 h-5 text-green-100" />
            </div>
            <p className="text-3xl font-bold">${totalROI.totalAnnualSavings.toLocaleString()}</p>
            <p className="text-green-100 text-sm mt-2">Before app cost</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-blue-100 text-sm font-medium">Net Annual Savings</p>
              <DollarSign className="w-5 h-5 text-blue-100" />
            </div>
            <p className="text-3xl font-bold">${totalROI.netAnnualSavings.toLocaleString()}</p>
            <p className="text-blue-100 text-sm mt-2">After ${totalROI.appAnnualCost.toLocaleString()} app cost</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-purple-100 text-sm font-medium">ROI</p>
              <BarChart3 className="w-5 h-5 text-purple-100" />
            </div>
            <p className="text-3xl font-bold">{totalROI.roi}%</p>
            <p className="text-purple-100 text-sm mt-2">Return on investment</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-orange-100 text-sm font-medium">Payback Period</p>
              <Clock className="w-5 h-5 text-orange-100" />
            </div>
            <p className="text-3xl font-bold">{totalROI.paybackMonths} mo</p>
            <p className="text-orange-100 text-sm mt-2">Time to break even</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Savings Breakdown</CardTitle>
            <CardDescription>Annual savings by category</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <RechartsPie>
                <Pie
                  data={savingsBreakdownData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: $${(value/1000).toFixed(0)}k`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {savingsBreakdownData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
              </RechartsPie>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>5-Year Projection</CardTitle>
            <CardDescription>Cumulative net savings over time</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <RechartsLine>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="year" />
                <YAxis />
                <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="savings" 
                  data={fiveYearProjectionData}
                  stroke="#3b82f6" 
                  strokeWidth={2}
                  name="Cumulative Savings"
                />
              </RechartsLine>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Calculators */}
      <Tabs defaultValue="realcosts" className="space-y-4">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="realcosts">Your Actual Costs</TabsTrigger>
          <TabsTrigger value="staff">Staff Comparison</TabsTrigger>
          <TabsTrigger value="insurance">Insurance</TabsTrigger>
          <TabsTrigger value="osha">OSHA Fines</TabsTrigger>
          <TabsTrigger value="productivity">Productivity</TabsTrigger>
          <TabsTrigger value="medical">Medical Costs</TabsTrigger>
        </TabsList>

        {/* NEW: Real Company Costs Calculator */}
        <TabsContent value="realcosts">
          <div className="space-y-6">
            {/* Summary Card */}
            <Card className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white">
              <CardHeader>
                <CardTitle className="text-2xl flex items-center">
                  <Calculator className="w-6 h-6 mr-3" />
                  Your Real Bottom Line Impact
                </CardTitle>
                <CardDescription className="text-indigo-100">
                  Based on your actual costs and injury data
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-white/10 backdrop-blur rounded-lg p-4">
                    <p className="text-indigo-100 text-sm mb-1">Current Safety Costs</p>
                    <p className="text-3xl font-bold">${realCompanyCosts.totalCurrentSafetyCosts.toLocaleString()}</p>
                    <p className="text-indigo-100 text-xs mt-1">Per year</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur rounded-lg p-4">
                    <p className="text-indigo-100 text-sm mb-1">Projected with App</p>
                    <p className="text-3xl font-bold">${realCompanyCosts.projectedCostsWithApp.toLocaleString()}</p>
                    <p className="text-indigo-100 text-xs mt-1">Per year</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur rounded-lg p-4">
                    <p className="text-indigo-100 text-sm mb-1">Annual Savings</p>
                    <p className="text-3xl font-bold text-green-300">${realCompanyCosts.totalAnnualSavings.toLocaleString()}</p>
                    <p className="text-indigo-100 text-xs mt-1">Net benefit</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur rounded-lg p-4">
                    <p className="text-indigo-100 text-sm mb-1">ROI</p>
                    <p className="text-3xl font-bold text-yellow-300">{realCompanyCosts.roi}%</p>
                    <p className="text-indigo-100 text-xs mt-1">Payback: {realCompanyCosts.paybackMonths} months</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Input Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <DollarSign className="w-5 h-5 mr-2 text-indigo-600" />
                  Enter Your Actual Company Data
                </CardTitle>
                <CardDescription>
                  Provide your real numbers for an accurate ROI calculation
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Safety Staff Costs */}
                <div>
                  <h3 className="font-semibold text-lg mb-4 text-gray-900 flex items-center">
                    <Users className="w-5 h-5 mr-2 text-blue-600" />
                    Safety Employee Costs
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label>Number of Safety Employees</Label>
                      <Input
                        type="number"
                        value={companyData.numSafetyEmployees}
                        onChange={(e) => setCompanyData({...companyData, numSafetyEmployees: parseInt(e.target.value) || 0})}
                      />
                      <p className="text-xs text-gray-500 mt-1">Full-time safety staff</p>
                    </div>
                    <div>
                      <Label>Average Salary per Employee</Label>
                      <Input
                        type="number"
                        value={companyData.avgSafetyEmployeeSalary}
                        onChange={(e) => setCompanyData({...companyData, avgSafetyEmployeeSalary: parseInt(e.target.value) || 0})}
                      />
                      <p className="text-xs text-gray-500 mt-1">Annual salary (before benefits)</p>
                    </div>
                    <div>
                      <Label>Benefits Percentage</Label>
                      <Input
                        type="number"
                        value={companyData.safetyEmployeeBenefitsPercentage}
                        onChange={(e) => setCompanyData({...companyData, safetyEmployeeBenefitsPercentage: parseInt(e.target.value) || 0})}
                      />
                      <p className="text-xs text-gray-500 mt-1">% of salary (typically 25-40%)</p>
                    </div>
                  </div>
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                    <p className="text-sm text-blue-900">
                      <strong>Total Safety Staff Cost:</strong> ${realCompanyCosts.totalSafetyStaffCost.toLocaleString()}/year
                    </p>
                  </div>
                </div>

                {/* Current Year Injuries */}
                <div>
                  <h3 className="font-semibold text-lg mb-4 text-gray-900 flex items-center">
                    <AlertTriangle className="w-5 h-5 mr-2 text-orange-600" />
                    Current Year Injuries
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div>
                      <Label>Minor Injuries</Label>
                      <Input
                        type="number"
                        value={companyData.currentYearInjuries.minorInjuries}
                        onChange={(e) => setCompanyData({
                          ...companyData,
                          currentYearInjuries: {...companyData.currentYearInjuries, minorInjuries: parseInt(e.target.value) || 0}
                        })}
                      />
                      <p className="text-xs text-gray-500 mt-1">First aid only</p>
                    </div>
                    <div>
                      <Label>Medical Treatment</Label>
                      <Input
                        type="number"
                        value={companyData.currentYearInjuries.medicalTreatment}
                        onChange={(e) => setCompanyData({
                          ...companyData,
                          currentYearInjuries: {...companyData.currentYearInjuries, medicalTreatment: parseInt(e.target.value) || 0}
                        })}
                      />
                      <p className="text-xs text-gray-500 mt-1">No lost time</p>
                    </div>
                    <div>
                      <Label>Lost Time</Label>
                      <Input
                        type="number"
                        value={companyData.currentYearInjuries.lostTimeInjuries}
                        onChange={(e) => setCompanyData({
                          ...companyData,
                          currentYearInjuries: {...companyData.currentYearInjuries, lostTimeInjuries: parseInt(e.target.value) || 0}
                        })}
                      />
                      <p className="text-xs text-gray-500 mt-1">Days away</p>
                    </div>
                    <div>
                      <Label>Serious Injuries</Label>
                      <Input
                        type="number"
                        value={companyData.currentYearInjuries.seriousInjuries}
                        onChange={(e) => setCompanyData({
                          ...companyData,
                          currentYearInjuries: {...companyData.currentYearInjuries, seriousInjuries: parseInt(e.target.value) || 0}
                        })}
                      />
                      <p className="text-xs text-gray-500 mt-1">Hospitalization</p>
                    </div>
                    <div>
                      <Label>Fatalities</Label>
                      <Input
                        type="number"
                        value={companyData.currentYearInjuries.fatalities}
                        onChange={(e) => setCompanyData({
                          ...companyData,
                          currentYearInjuries: {...companyData.currentYearInjuries, fatalities: parseInt(e.target.value) || 0}
                        })}
                      />
                      <p className="text-xs text-gray-500 mt-1">Deaths</p>
                    </div>
                  </div>
                  <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded">
                    <p className="text-sm text-orange-900">
                      <strong>Current Year Injury Costs:</strong> ${realCompanyCosts.currentInjuryCosts.toLocaleString()}
                    </p>
                  </div>
                </div>

                {/* Previous Year Injuries (Baseline) */}
                <div>
                  <h3 className="font-semibold text-lg mb-4 text-gray-900 flex items-center">
                    <Clock className="w-5 h-5 mr-2 text-gray-600" />
                    Previous Year Injuries (Baseline)
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div>
                      <Label>Minor Injuries</Label>
                      <Input
                        type="number"
                        value={companyData.previousYearInjuries.minorInjuries}
                        onChange={(e) => setCompanyData({
                          ...companyData,
                          previousYearInjuries: {...companyData.previousYearInjuries, minorInjuries: parseInt(e.target.value) || 0}
                        })}
                      />
                    </div>
                    <div>
                      <Label>Medical Treatment</Label>
                      <Input
                        type="number"
                        value={companyData.previousYearInjuries.medicalTreatment}
                        onChange={(e) => setCompanyData({
                          ...companyData,
                          previousYearInjuries: {...companyData.previousYearInjuries, medicalTreatment: parseInt(e.target.value) || 0}
                        })}
                      />
                    </div>
                    <div>
                      <Label>Lost Time</Label>
                      <Input
                        type="number"
                        value={companyData.previousYearInjuries.lostTimeInjuries}
                        onChange={(e) => setCompanyData({
                          ...companyData,
                          previousYearInjuries: {...companyData.previousYearInjuries, lostTimeInjuries: parseInt(e.target.value) || 0}
                        })}
                      />
                    </div>
                    <div>
                      <Label>Serious Injuries</Label>
                      <Input
                        type="number"
                        value={companyData.previousYearInjuries.seriousInjuries}
                        onChange={(e) => setCompanyData({
                          ...companyData,
                          previousYearInjuries: {...companyData.previousYearInjuries, seriousInjuries: parseInt(e.target.value) || 0}
                        })}
                      />
                    </div>
                    <div>
                      <Label>Fatalities</Label>
                      <Input
                        type="number"
                        value={companyData.previousYearInjuries.fatalities}
                        onChange={(e) => setCompanyData({
                          ...companyData,
                          previousYearInjuries: {...companyData.previousYearInjuries, fatalities: parseInt(e.target.value) || 0}
                        })}
                      />
                    </div>
                  </div>
                  <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded">
                    <p className="text-sm text-gray-900">
                      <strong>Previous Year Injury Costs:</strong> ${realCompanyCosts.previousInjuryCosts.toLocaleString()}
                      {realCompanyCosts.injuryReductionSavings > 0 && (
                        <span className="ml-2 text-green-600 font-semibold">
                          (Savings: ${realCompanyCosts.injuryReductionSavings.toLocaleString()} from reduction)
                        </span>
                      )}
                    </p>
                  </div>
                </div>

                {/* Injury Cost Assumptions */}
                <div>
                  <h3 className="font-semibold text-lg mb-4 text-gray-900 flex items-center">
                    <Heart className="w-5 h-5 mr-2 text-red-600" />
                    Your Cost Per Injury Type
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Adjust these to match your actual injury costs (including medical, workers comp, lost productivity, etc.)
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div>
                      <Label>Minor Injury</Label>
                      <Input
                        type="number"
                        value={companyData.injuryCosts.minorInjuryCost}
                        onChange={(e) => setCompanyData({
                          ...companyData,
                          injuryCosts: {...companyData.injuryCosts, minorInjuryCost: parseInt(e.target.value) || 0}
                        })}
                      />
                      <p className="text-xs text-gray-500 mt-1">Default: $1,500</p>
                    </div>
                    <div>
                      <Label>Medical Treatment</Label>
                      <Input
                        type="number"
                        value={companyData.injuryCosts.medicalTreatmentCost}
                        onChange={(e) => setCompanyData({
                          ...companyData,
                          injuryCosts: {...companyData.injuryCosts, medicalTreatmentCost: parseInt(e.target.value) || 0}
                        })}
                      />
                      <p className="text-xs text-gray-500 mt-1">Default: $42,000</p>
                    </div>
                    <div>
                      <Label>Lost Time</Label>
                      <Input
                        type="number"
                        value={companyData.injuryCosts.lostTimeInjuryCost}
                        onChange={(e) => setCompanyData({
                          ...companyData,
                          injuryCosts: {...companyData.injuryCosts, lostTimeInjuryCost: parseInt(e.target.value) || 0}
                        })}
                      />
                      <p className="text-xs text-gray-500 mt-1">Default: $47,000</p>
                    </div>
                    <div>
                      <Label>Serious Injury</Label>
                      <Input
                        type="number"
                        value={companyData.injuryCosts.seriousInjuryCost}
                        onChange={(e) => setCompanyData({
                          ...companyData,
                          injuryCosts: {...companyData.injuryCosts, seriousInjuryCost: parseInt(e.target.value) || 0}
                        })}
                      />
                      <p className="text-xs text-gray-500 mt-1">Default: $1.4M</p>
                    </div>
                    <div>
                      <Label>Fatality</Label>
                      <Input
                        type="number"
                        value={companyData.injuryCosts.fatalityCost}
                        onChange={(e) => setCompanyData({
                          ...companyData,
                          injuryCosts: {...companyData.injuryCosts, fatalityCost: parseInt(e.target.value) || 0}
                        })}
                      />
                      <p className="text-xs text-gray-500 mt-1">Default: $4M</p>
                    </div>
                  </div>
                </div>

                {/* Additional Costs */}
                <div>
                  <h3 className="font-semibold text-lg mb-4 text-gray-900 flex items-center">
                    <FileText className="w-5 h-5 mr-2 text-purple-600" />
                    Other Safety Costs
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label>Annual Insurance Premium</Label>
                      <Input
                        type="number"
                        value={companyData.currentInsurancePremium}
                        onChange={(e) => setCompanyData({...companyData, currentInsurancePremium: parseInt(e.target.value) || 0})}
                      />
                      <p className="text-xs text-gray-500 mt-1">Workers compensation insurance</p>
                    </div>
                    <div>
                      <Label>OSHA Fines (Last Year)</Label>
                      <Input
                        type="number"
                        value={companyData.oshaFinesLastYear}
                        onChange={(e) => setCompanyData({...companyData, oshaFinesLastYear: parseInt(e.target.value) || 0})}
                      />
                      <p className="text-xs text-gray-500 mt-1">Baseline for comparison</p>
                    </div>
                    <div>
                      <Label>OSHA Fines (This Year)</Label>
                      <Input
                        type="number"
                        value={companyData.oshaFinesThisYear}
                        onChange={(e) => setCompanyData({...companyData, oshaFinesThisYear: parseInt(e.target.value) || 0})}
                      />
                      <p className="text-xs text-gray-500 mt-1">Current year fines</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Detailed Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <BarChart3 className="w-5 h-5 mr-2 text-green-600" />
                  Detailed Cost vs Savings Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Current Costs */}
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <h4 className="font-semibold text-red-900 mb-3">Current Annual Costs</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>Safety Staff:</span>
                          <span className="font-semibold">${realCompanyCosts.totalSafetyStaffCost.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Injury Costs:</span>
                          <span className="font-semibold">${realCompanyCosts.currentInjuryCosts.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Insurance Premium:</span>
                          <span className="font-semibold">${companyData.currentInsurancePremium.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>OSHA Fines:</span>
                          <span className="font-semibold">${companyData.oshaFinesThisYear.toLocaleString()}</span>
                        </div>
                        <div className="border-t border-red-300 pt-2 mt-2 flex justify-between font-bold text-base">
                          <span>TOTAL:</span>
                          <span className="text-red-700">${realCompanyCosts.totalCurrentSafetyCosts.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>

                    {/* Projected Costs with App */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h4 className="font-semibold text-green-900 mb-3">Projected with WSafe AI</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>App Subscription:</span>
                          <span className="font-semibold">${realCompanyCosts.appAnnualCost.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Injury Costs (50% reduction):</span>
                          <span className="font-semibold">${(realCompanyCosts.currentInjuryCosts * 0.5).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Insurance (15% reduction):</span>
                          <span className="font-semibold">${(companyData.currentInsurancePremium * 0.85).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>OSHA Fines (70% reduction):</span>
                          <span className="font-semibold">${(companyData.oshaFinesThisYear * 0.3).toLocaleString()}</span>
                        </div>
                        <div className="border-t border-green-300 pt-2 mt-2 flex justify-between font-bold text-base">
                          <span>TOTAL:</span>
                          <span className="text-green-700">${realCompanyCosts.projectedCostsWithApp.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Savings Summary */}
                  <div className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg p-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-blue-100 text-sm mb-1">Staff Savings</p>
                        <p className="text-2xl font-bold">${realCompanyCosts.staffCostSavings.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-blue-100 text-sm mb-1">Injury Savings</p>
                        <p className="text-2xl font-bold">${Math.round(realCompanyCosts.injuryCostSavings).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-blue-100 text-sm mb-1">Insurance Savings</p>
                        <p className="text-2xl font-bold">${Math.round(realCompanyCosts.insuranceSavings).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-blue-100 text-sm mb-1">Fine Savings</p>
                        <p className="text-2xl font-bold">${Math.round(realCompanyCosts.fineSavings).toLocaleString()}</p>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-white/20">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-blue-100 text-sm">Total Annual Savings</p>
                          <p className="text-4xl font-bold">${realCompanyCosts.totalAnnualSavings.toLocaleString()}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-blue-100 text-sm">5-Year Projection</p>
                          <p className="text-3xl font-bold">${realCompanyCosts.fiveYearSavings.toLocaleString()}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Staff Comparison Calculator */}
        <TabsContent value="staff">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Users className="w-5 h-5 mr-2 text-blue-600" />
                Staff Cost Comparison
              </CardTitle>
              <CardDescription>
                Compare the cost of using our app vs. hiring dedicated safety employees
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Total Employees</Label>
                  <Input
                    type="number"
                    value={staffing.totalEmployees}
                    onChange={(e) => setStaffing({...staffing, totalEmployees: parseInt(e.target.value) || 0})}
                  />
                  <p className="text-xs text-gray-500 mt-1">Number of employees in your company</p>
                </div>
                <div>
                  <Label>Monthly App Cost</Label>
                  <Input
                    type="number"
                    value={staffing.appSubscriptionCost}
                    onChange={(e) => setStaffing({...staffing, appSubscriptionCost: parseInt(e.target.value) || 0})}
                  />
                  <p className="text-xs text-gray-500 mt-1">Your monthly subscription cost</p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 space-y-4">
                <h3 className="font-semibold text-lg text-blue-900">Cost Comparison Results</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4 border border-blue-100">
                    <p className="text-sm text-gray-600 mb-1">Required Safety Staff</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {staffComparison.requiredSafetyStaff} {staffComparison.requiredSafetyStaff === 1 ? 'Employee' : 'Employees'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      OSHA compliance standard for {staffing.totalEmployees} employees
                    </p>
                  </div>

                  <div className="bg-white rounded-lg p-4 border border-blue-100">
                    <p className="text-sm text-gray-600 mb-1">Traditional Annual Cost</p>
                    <p className="text-2xl font-bold text-red-600">
                      ${staffComparison.traditionalAnnualCost.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      ${(staffComparison.traditionalAnnualCost / staffComparison.requiredSafetyStaff).toLocaleString()}/employee (salary + benefits)
                    </p>
                  </div>

                  <div className="bg-white rounded-lg p-4 border border-blue-100">
                    <p className="text-sm text-gray-600 mb-1">App Annual Cost</p>
                    <p className="text-2xl font-bold text-green-600">
                      ${staffComparison.appAnnualCost.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      ${staffing.appSubscriptionCost}/month × 12 months
                    </p>
                  </div>

                  <div className="bg-green-100 rounded-lg p-4 border border-green-200">
                    <p className="text-sm text-green-700 mb-1">Annual Savings</p>
                    <p className="text-2xl font-bold text-green-700">
                      ${staffComparison.annualSavings.toLocaleString()}
                    </p>
                    <p className="text-xs text-green-600 mt-1">
                      {staffComparison.savingsPercentage}% cost reduction
                    </p>
                  </div>
                </div>

                <div className="border-t border-blue-200 pt-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-700">5-Year Savings Projection</p>
                    <p className="text-xl font-bold text-green-600">
                      ${staffComparison.fiveYearSavings.toLocaleString()}
                    </p>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Over 5 years, save enough to hire {Math.floor(staffComparison.fiveYearSavings / 104000)} additional safety employees if needed
                  </p>
                </div>

                <div className="bg-white rounded-lg p-4 border border-blue-200">
                  <h4 className="font-semibold text-blue-900 mb-2">Industry Standards (Construction)</h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>10-49 employees: 1 safety employee required</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>50+ employees: 1 additional safety employee per 50 workers</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Average safety employee cost: $80,000 salary + 30% benefits = $104,000/year</span>
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Insurance Premium Calculator */}
        <TabsContent value="insurance">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Shield className="w-5 h-5 mr-2 text-green-600" />
                Insurance Premium Calculator
              </CardTitle>
              <CardDescription>
                Calculate savings from reduced insurance premiums based on improved safety record
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Current Annual Premium</Label>
                  <Input
                    type="number"
                    value={insurance.currentPremium}
                    onChange={(e) => setInsurance({...insurance, currentPremium: parseInt(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <Label>Number of Employees</Label>
                  <Input
                    type="number"
                    value={insurance.employeeCount}
                    onChange={(e) => setInsurance({...insurance, employeeCount: parseInt(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <Label>Incidents Last Year</Label>
                  <Input
                    type="number"
                    value={insurance.incidentsLastYear}
                    onChange={(e) => setInsurance({...insurance, incidentsLastYear: parseInt(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <Label>Incidents This Year (with app)</Label>
                  <Input
                    type="number"
                    value={insurance.incidentsThisYear}
                    onChange={(e) => setInsurance({...insurance, incidentsThisYear: parseInt(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <Label>Violations Prevented</Label>
                  <Input
                    type="number"
                    value={insurance.violationsPrevented}
                    onChange={(e) => setInsurance({...insurance, violationsPrevented: parseInt(e.target.value) || 0})}
                  />
                </div>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-6 space-y-3">
                <h3 className="font-semibold text-lg text-green-900">Estimated Premium Savings</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Annual Savings</p>
                    <p className="text-2xl font-bold text-green-600">
                      ${insuranceSavings.annualSavings.toLocaleString()}
                    </p>
                    <Badge className="mt-1 bg-green-100 text-green-700">
                      {insuranceSavings.reductionPercentage}% reduction
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">3-Year Savings</p>
                    <p className="text-2xl font-bold text-green-600">
                      ${insuranceSavings.threeYearSavings.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">5-Year Savings</p>
                    <p className="text-2xl font-bold text-green-600">
                      ${insuranceSavings.fiveYearSavings.toLocaleString()}
                    </p>
                  </div>
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  * Based on industry averages: 2-5% reduction per year without incidents, plus 1% per violation prevented
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* OSHA Fine Calculator */}
        <TabsContent value="osha">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <AlertTriangle className="w-5 h-5 mr-2 text-orange-600" />
                OSHA Fine Calculator
              </CardTitle>
              <CardDescription>
                Calculate money saved from prevented OSHA violations (2025 penalty amounts)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Serious Violations Prevented</Label>
                  <Input
                    type="number"
                    value={oshaFines.seriousViolations}
                    onChange={(e) => setOshaFines({...oshaFines, seriousViolations: parseInt(e.target.value) || 0})}
                  />
                  <p className="text-xs text-gray-500 mt-1">$16,131 per violation</p>
                </div>
                <div>
                  <Label>Willful Violations Prevented</Label>
                  <Input
                    type="number"
                    value={oshaFines.willfulViolations}
                    onChange={(e) => setOshaFines({...oshaFines, willfulViolations: parseInt(e.target.value) || 0})}
                  />
                  <p className="text-xs text-gray-500 mt-1">$161,323 per violation</p>
                </div>
                <div>
                  <Label>Repeated Violations Prevented</Label>
                  <Input
                    type="number"
                    value={oshaFines.repeatedViolations}
                    onChange={(e) => setOshaFines({...oshaFines, repeatedViolations: parseInt(e.target.value) || 0})}
                  />
                  <p className="text-xs text-gray-500 mt-1">$161,323 per violation</p>
                </div>
                <div>
                  <Label>Failure to Abate (Days Prevented)</Label>
                  <Input
                    type="number"
                    value={oshaFines.failureToAbateDays}
                    onChange={(e) => setOshaFines({...oshaFines, failureToAbateDays: parseInt(e.target.value) || 0})}
                  />
                  <p className="text-xs text-gray-500 mt-1">$16,131 per day</p>
                </div>
              </div>

              <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 space-y-3">
                <h3 className="font-semibold text-lg text-orange-900">Total OSHA Fines Avoided</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-gray-600">Serious</p>
                    <p className="text-lg font-bold text-orange-600">
                      ${oshaFineSavings.seriousSavings.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Willful</p>
                    <p className="text-lg font-bold text-orange-600">
                      ${oshaFineSavings.willfulSavings.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Repeated</p>
                    <p className="text-lg font-bold text-orange-600">
                      ${oshaFineSavings.repeatedSavings.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Failure to Abate</p>
                    <p className="text-lg font-bold text-orange-600">
                      ${oshaFineSavings.failureToAbateSavings.toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="border-t border-orange-200 pt-3 mt-3">
                  <p className="text-sm text-gray-600">Total Fines Avoided</p>
                  <p className="text-3xl font-bold text-orange-600">
                    ${oshaFineSavings.totalSavings.toLocaleString()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Productivity Gains Calculator */}
        <TabsContent value="productivity">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Clock className="w-5 h-5 mr-2 text-purple-600" />
                Productivity Gains Calculator
              </CardTitle>
              <CardDescription>
                Calculate time and cost savings from automated documentation and tracking
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Audits per Month</Label>
                  <Input
                    type="number"
                    value={productivity.auditsPerMonth}
                    onChange={(e) => setProductivity({...productivity, auditsPerMonth: parseInt(e.target.value) || 0})}
                  />
                  <p className="text-xs text-gray-500 mt-1">Saves ~6 hours per audit</p>
                </div>
                <div>
                  <Label>OSHA Log Updates per Month</Label>
                  <Input
                    type="number"
                    value={productivity.oshaLogsPerMonth}
                    onChange={(e) => setProductivity({...productivity, oshaLogsPerMonth: parseInt(e.target.value) || 0})}
                  />
                  <p className="text-xs text-gray-500 mt-1">Saves ~1.5 hours per update</p>
                </div>
                <div>
                  <Label>Reports Generated per Month</Label>
                  <Input
                    type="number"
                    value={productivity.reportsPerMonth}
                    onChange={(e) => setProductivity({...productivity, reportsPerMonth: parseInt(e.target.value) || 0})}
                  />
                  <p className="text-xs text-gray-500 mt-1">Saves ~3.75 hours per report</p>
                </div>
                <div>
                  <Label>Training Sessions per Month</Label>
                  <Input
                    type="number"
                    value={productivity.trainingSessionsPerMonth}
                    onChange={(e) => setProductivity({...productivity, trainingSessionsPerMonth: parseInt(e.target.value) || 0})}
                  />
                  <p className="text-xs text-gray-500 mt-1">Saves ~2.5 hours per session</p>
                </div>
                <div className="md:col-span-2">
                  <Label>Safety Manager Hourly Rate</Label>
                  <Input
                    type="number"
                    value={productivity.safetyManagerHourlyRate}
                    onChange={(e) => setProductivity({...productivity, safetyManagerHourlyRate: parseInt(e.target.value) || 0})}
                  />
                  <p className="text-xs text-gray-500 mt-1">Average rate: $50-75/hour</p>
                </div>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 space-y-3">
                <h3 className="font-semibold text-lg text-purple-900">Productivity Savings</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Monthly Hours Saved</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {productivityGains.monthlyHoursSaved} hours
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      ${productivityGains.monthlySavings.toLocaleString()}/month
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Annual Savings</p>
                    <p className="text-2xl font-bold text-purple-600">
                      ${productivityGains.annualSavings.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {productivityGains.annualHoursSaved} hours saved per year
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Medical Costs Calculator */}
        <TabsContent value="medical">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Heart className="w-5 h-5 mr-2 text-red-600" />
                Medical Costs Prevention Tracker
              </CardTitle>
              <CardDescription>
                Track and calculate avoided injury costs (based on OSHA/NIOSH industry data)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Minor Injuries Prevented</Label>
                  <Input
                    type="number"
                    value={medicalCosts.minorInjuriesPrevented}
                    onChange={(e) => setMedicalCosts({...medicalCosts, minorInjuriesPrevented: parseInt(e.target.value) || 0})}
                  />
                  <p className="text-xs text-gray-500 mt-1">First aid only - $1,500 avg</p>
                </div>
                <div>
                  <Label>Medical Treatment Prevented</Label>
                  <Input
                    type="number"
                    value={medicalCosts.medicalTreatmentPrevented}
                    onChange={(e) => setMedicalCosts({...medicalCosts, medicalTreatmentPrevented: parseInt(e.target.value) || 0})}
                  />
                  <p className="text-xs text-gray-500 mt-1">Doctor visit required - $42,000 avg</p>
                </div>
                <div>
                  <Label>Lost Time Injuries Prevented</Label>
                  <Input
                    type="number"
                    value={medicalCosts.lostTimeInjuriesPrevented}
                    onChange={(e) => setMedicalCosts({...medicalCosts, lostTimeInjuriesPrevented: parseInt(e.target.value) || 0})}
                  />
                  <p className="text-xs text-gray-500 mt-1">Days away from work - $47,000 avg</p>
                </div>
                <div>
                  <Label>Serious Injuries Prevented</Label>
                  <Input
                    type="number"
                    value={medicalCosts.seriousInjuriesPrevented}
                    onChange={(e) => setMedicalCosts({...medicalCosts, seriousInjuriesPrevented: parseInt(e.target.value) || 0})}
                  />
                  <p className="text-xs text-gray-500 mt-1">Permanent disability - $1.4M avg</p>
                </div>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-6 space-y-3">
                <h3 className="font-semibold text-lg text-red-900">Total Medical Costs Avoided</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-gray-600">Minor</p>
                    <p className="text-lg font-bold text-red-600">
                      ${medicalCostSavings.minorSavings.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Medical</p>
                    <p className="text-lg font-bold text-red-600">
                      ${medicalCostSavings.medicalSavings.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Lost Time</p>
                    <p className="text-lg font-bold text-red-600">
                      ${medicalCostSavings.lostTimeSavings.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Serious</p>
                    <p className="text-lg font-bold text-red-600">
                      ${medicalCostSavings.seriousSavings.toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="border-t border-red-200 pt-3 mt-3">
                  <p className="text-sm text-gray-600">Total Medical Costs Avoided</p>
                  <p className="text-3xl font-bold text-red-600">
                    ${medicalCostSavings.totalSavings.toLocaleString()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Bottom Summary */}
      <Card className="bg-gradient-to-r from-blue-50 to-green-50">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">5-Year Total Net Savings</h3>
              <p className="text-4xl font-bold text-green-600">
                ${totalROI.fiveYearProjection.toLocaleString()}
              </p>
              <p className="text-sm text-gray-600 mt-2">
                With {totalROI.roi}% ROI and {totalROI.paybackMonths} month payback period
              </p>
            </div>
            <Calculator className="w-24 h-24 text-blue-200" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ROIDashboard;
